import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import ItemList from '../../components/ItemList/ItemList';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

// API
import { useAlgoritmoTesteService } from '../../services/algoritmoTesteServices';
import { useTesteService } from '../../services/testeServices';

// Estilos
import styles from './algoritmostestepage.module.css';

function AlgoritmosTestePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showModal } = useModal();
  const { showLoader, hideLoader } = useLoader();

  const [algoritmos, setAlgoritmos] = useState([]);

  const [seletorAberto, setSeletorAberto] = useState(false);
  const [testesSelect, setTestesSelect] = useState([]);
  const [testeSelecionado, setTesteSelecionado] = useState('');

  const isLoadingListRef = useRef(false); // <-- lock contra chamadas concorrentes

  const {
    listarAlgoritmos,
    deletarAlgoritmo,
    listarAlgoritmosPorTeste,
    obterAlgoritmoPorId,
    publicarAlgoritmo, // backend publica sem alterar o JSON
  } = useAlgoritmoTesteService();

  const { listarTestes } = useTesteService();

  // --- Carregar lista (com lock para evitar loops) ---
  const carregarAlgoritmos = useCallback(async () => {
    if (isLoadingListRef.current) return;
    isLoadingListRef.current = true;

    showLoader('Carregando algoritmos');
    try {
      const response = await listarAlgoritmos();

      if (response?.success) {
        console.log(response.data);
        const items = (response.data || []).map((a) => {
          const status = a.status; // 'draft' | 'published' | 'archived'
          const id = a.idEncoded || a.id;
          const testeId = a.testeIdEncoded || a.teste_id || a.testeId || null;

          return {
            id,
            testeId,
            name: `${a.titulo} (v${a.versao} • ${status})`,
            status,
            is_current: a.is_current ?? 0,
            canPublish: status === 'draft', // <-- só rascunhos podem publicar
          };
        });

        setAlgoritmos(items);
      } else {
        showToast(response?.message || 'Erro ao carregar algoritmos', 3000, 'error');
      }
    } catch {
      showToast('Erro ao buscar algoritmos', 3000, 'error');
    } finally {
      hideLoader();
      isLoadingListRef.current = false;
    }
  }, [listarAlgoritmos, showLoader, hideLoader, showToast]);

  useEffect(() => {
    // chama apenas uma vez; lock impede reentrância (inclusive em StrictMode dev)
    carregarAlgoritmos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === EDITAR: /algoritmos/:idTeste/atualizar/:idAlgoritmo ===
  const handleEditar = async (item) => {
    if (item?.testeId) {
      navigate(`/algoritmos/${item.testeId}/atualizar/${item.id}`);
      return;
    }

    try {
      showLoader('Carregando algoritmo');
      const res = await obterAlgoritmoPorId(item.id);
      if (res?.success && res?.data) {
        const testeId =
          res.data.testeIdEncoded ||
          res.data.teste_id ||
          res.data.testeId ||
          res.data.idTeste;

        if (testeId) {
          navigate(`/algoritmos/${testeId}/atualizar/${item.id}`);
        } else {
          showToast('Não foi possível identificar o teste deste algoritmo.', 3500, 'error');
        }
      } else {
        showToast(res?.message || 'Algoritmo não encontrado.', 3500, 'error');
      }
    } catch {
      showToast('Erro ao carregar dados do algoritmo.', 3000, 'error');
    } finally {
      hideLoader();
    }
  };

  const handleExcluir = (item) => {
    if(item.status === 'published') {
      showToast('Não é permitido excluir versões publicadas.', 3000);
    } else {
      showModal(
        `Excluir ${item.name}?`,
        'Essa ação não poderá ser desfeita.',
        async () => {
          showLoader('Excluindo algoritmo');

          try {
            const response = await deletarAlgoritmo(item.id);
            if (response?.success) {
              setAlgoritmos((prev) => prev.filter((el) => el.id !== item.id));
              showToast('Algoritmo excluído com sucesso!', 3000, 'success');
            } else {
              showToast(response?.message || 'Erro ao excluir algoritmo', 3000, 'error');
            }
          } catch {
            showToast('Erro ao conectar com o servidor', 3000, 'error');
          } finally {
            hideLoader();
          }
        },
        () => showToast('Exclusão cancelada.', 2000)
      );
    }

  };

  // === ADD via seletor de testes ===
  const handleAdd = async () => {
    showLoader('Carregando testes');

    try {
      const res = await listarTestes();

      if (!res?.success) {
        showToast(res?.message || 'Não foi possível carregar os testes', 3000, 'error');
        hideLoader();
        return;
      }

      const testes = (res.data || []).map((t) => ({ id: t.id, nome: t.nome }));
      setTestesSelect(testes);
      setTesteSelecionado(testes[0]?.id || '');
      setSeletorAberto(true);
    } catch {
      showToast('Erro ao carregar testes', 3000, 'error');
    } finally {
      hideLoader();
    }
  };

  const confirmarSeletor = async () => {
    if (!testeSelecionado) {
      showToast('Selecione um teste.', 2500, 'info');
      return;
    }

    setSeletorAberto(false);
    showLoader('Verificando vínculos');

    try {
      const check = await listarAlgoritmosPorTeste({ testeId: testeSelecionado, limit: 1 });

      if (check?.success) {
        const hasAny = (check.data?.length || 0) > 0;

        if (hasAny) {
          const idAlgoritmoMaisRecente = check.data[0].idEncoded || check.data[0].id;
          navigate(`/algoritmos/${testeSelecionado}/atualizar/${idAlgoritmoMaisRecente}`);
        } else {
          navigate(`/algoritmos/${testeSelecionado}/adicionarAlgoritmo`);
        }
      } else {
        showToast(check?.message || 'Falha ao verificar algoritmos do teste', 3000, 'error');
      }
    } catch {
      showToast('Erro ao verificar vínculos do teste', 3000, 'error');
    } finally {
      hideLoader();
    }
  };

  const cancelarSeletor = () => setSeletorAberto(false);

  const handleSelecionar = (item) => {
    // espaço para ação futura (ex.: abrir detalhes)
    console.log('Algoritmo selecionado:', item);
  };

  // === Publicar: apenas se draft ===
  const handlePublicar = async (item) => {
    if (item?.status !== 'draft') {
      showToast('Apenas algoritmos em rascunho podem ser publicados.', 3000, 'info');
      return;
    }

    try {
      showLoader('Publicando algoritmo…');
      const res = await publicarAlgoritmo(item.id);
      if (res?.success) {
        showToast('Algoritmo publicado!', 2500, 'success');
        await carregarAlgoritmos();
      } else {
        showToast(res?.message || 'Falha ao publicar algoritmo', 3500, 'error');
      }
    } catch {
      showToast('Erro ao publicar algoritmo', 3500, 'error');
    } finally {
      hideLoader();
    }
  };

  return (
    <div className={styles.AlgoritmosTestePage}>
      <div className={styles.containerTop}>
        <Breadcrumbs />
        <h1 className={styles.title}>Algoritmos de Testes</h1>
        <FriendlyPhrase page="algoritmos" />
      </div>

      <div className={styles.containerList}>
        <ItemList
          items={algoritmos}
          onAdd={handleAdd}
          onEdit={handleEditar}
          onDelete={handleExcluir}
          onClickItem={handleSelecionar}
          onPublish={handlePublicar}
        />
      </div>

      {seletorAberto && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>Vincular algoritmo a um teste</h2>

            {testesSelect.length > 0 ? (
              <>
                <div className={styles.modalInfo}>
                  <p>Escolha o teste ao qual deseja vincular o algoritmo:</p>
                  <select
                    className={styles.selectControl}
                    value={testeSelecionado}
                    onChange={(e) => setTesteSelecionado(e.target.value)}
                  >
                    <option value="" disabled>Selecionar</option>
                    {testesSelect.map((t) => (
                      <option key={t.id} value={t.id}>{t.nome}</option>
                    ))}
                  </select>
                  <small className={styles.mutedNote}>
                    Se o teste já tiver algoritmo, você será levado para a edição; caso contrário, para a criação.
                  </small>
                </div>

                <div className={styles.modalActions}>
                  <button className={styles.secondaryButton} onClick={cancelarSeletor}>
                    Cancelar
                  </button>
                  <button className={styles.primaryButton} onClick={confirmarSeletor}>
                    Continuar
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>Não há testes cadastrados para vincular.</p>
                <div className={styles.modalActions}>
                  <button className={styles.primaryButton} onClick={cancelarSeletor}>
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AlgoritmosTestePage;
